import { useCallback, useState, useMemo } from 'react';
import { produce } from 'immer';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableSkeletonRows,
} from '@antimatterhq/ui/components/simpleTable';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { DetectionCategoryDialog } from '@/components/dialog';
import { bumpMetadataVersion } from '@/utils/util';

export const DetectionCategories = () => {
  const { data, isPending } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig } = useUpdateWorkspaceConfig();
  const [activeCategory, setActiveCategory] = useState<string | undefined>(
    undefined,
  );
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();
  const [editIndex, setEditIndex] = useState<number | undefined>(undefined);
  const categories = useMemo(() => {
    return data?.spec.detectionRuleMetadata?.detectionCategories || [];
  }, [data]);

  const onSubmit = useCallback(
    async (updatedCategories: string[]) => {
      if (!data) {
        return;
      }

      const config = produce(data, (draft) => {
        if (!draft.spec.detectionRuleMetadata) {
          draft.spec.detectionRuleMetadata = { detectionCategories: [] };
        }
        draft.spec.detectionRuleMetadata.detectionCategories = [
          ...updatedCategories,
        ];
        bumpMetadataVersion(draft);
      });
      optimisticUpdate(config);

      await updateConfig(config, {
        toasts: {
          success: 'Detection categories updated',
          error: 'Failed to update detection categories',
        },
      });
    },
    [data, updateConfig, optimisticUpdate],
  );

  const addDetection = useCallback(() => {
    if (!activeCategory) {
      return;
    }
    const updatedCategories = [...categories, activeCategory];
    onSubmit(updatedCategories);
  }, [activeCategory, onSubmit, categories]);

  const updateDetection = useCallback(() => {
    if (!activeCategory) {
      return;
    }

    const updatedCategories = categories.map((o, index) =>
      index === editIndex ? activeCategory : o,
    );
    onSubmit(updatedCategories);

    setActiveCategory(undefined);
    setEditIndex(undefined);
  }, [activeCategory, editIndex, onSubmit, categories]);

  const removeDetection = useCallback(
    (category: string) => {
      const updatedCategories = categories.filter((o) => o !== category);
      onSubmit(updatedCategories);
    },
    [categories, onSubmit],
  );

  const onSubmitDetection = useCallback(async () => {
    if (editIndex !== undefined) {
      updateDetection();
    } else {
      addDetection();
    }
    setActiveCategory(undefined);
  }, [addDetection, updateDetection, editIndex]);

  return (
    <>
      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow hover={false}>
            <SimpleTableHead className="w-[250px] font-bold">
              Detection category
            </SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {!isPending &&
            categories.map((category, index) => (
              <SimpleTableRow key={category} className="h-[31px]">
                <SimpleTableCell
                  className="relative"
                  data-testid={`detection-category-${category}`}
                >
                  {category}
                  <div className="invisible absolute right-0 top-0 flex gap-1">
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:pencil"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => {
                        setActiveCategory(category);
                        setEditIndex(index);
                      }}
                      data-testid={`edit-detection-category-${category}-button`}
                    />
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:trash"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => removeDetection(category)}
                      data-testid={`remove-detection-category-${category}-button`}
                    />
                  </div>
                </SimpleTableCell>
              </SimpleTableRow>
            ))}
          {isPending && (
            <SimpleTableSkeletonRows cols={1} height={31} rows={5} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="lg" />

      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          leftIcon="radix-icons:plus"
          onClick={() => setActiveCategory('')}
          data-testid="add-detection-category-button"
        >
          Add a category
        </Button>
      </div>
      <DetectionCategoryDialog
        open={activeCategory !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setActiveCategory(undefined);
          }
        }}
        onSave={onSubmitDetection}
        category={activeCategory}
        setCategory={setActiveCategory}
        isEdit={editIndex !== undefined}
        existingCategories={categories}
      />
    </>
  );
};
